import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Button,
  Snackbar,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SaveIcon from '@mui/icons-material/Save';
import api from '../../api/axios';

interface Business {
  id: string;
  name: string;
  country: string;
  time_zone: string;
  created_at: string;
  updated_at: string;
}

const BusinessSettings: React.FC = () => {
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState<string | null>(null);
  const [currentBusinessId, setCurrentBusinessId] = useState<string>('');
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchBusinesses();
    fetchCurrentBusinessId();
  }, []);

  const fetchBusinesses = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/api/businesses');
      setBusinesses(response.data.businesses);
    } catch (err: any) {
      console.error('Error fetching businesses:', err);
      setError(err.response?.data?.error || 'Failed to fetch businesses');
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentBusinessId = async () => {
    try {
      const response = await api.get('/api/settings/business-id');
      setCurrentBusinessId(response.data.businessId || '');
    } catch (err: any) {
      console.error('Error fetching current business ID:', err);
    }
  };

  const copyToClipboard = (id: string) => {
    navigator.clipboard.writeText(id)
      .then(() => {
        setCopySuccess(`Business ID copied to clipboard: ${id}`);
        setTimeout(() => setCopySuccess(null), 3000);
      })
      .catch(err => {
        console.error('Failed to copy:', err);
      });
  };

  const handleSetAsDefault = (business: Business) => {
    setSelectedBusiness(business);
    setOpenDialog(true);
  };

  const handleConfirmSetDefault = async () => {
    if (!selectedBusiness) return;
    
    try {
      const response = await api.post('/api/settings/business-id', {
        businessId: selectedBusiness.id
      });
      
      setCurrentBusinessId(selectedBusiness.id);
      setSaveSuccess(`Business "${selectedBusiness.name}" set as default`);
      setTimeout(() => setSaveSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error setting default business:', err);
      setError(err.response?.data?.error || 'Failed to set default business');
    } finally {
      setOpenDialog(false);
      setSelectedBusiness(null);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedBusiness(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Cliniko Businesses
      </Typography>
      <Typography variant="body1" paragraph>
        Below are the businesses available in your Cliniko account. Set one as default to use for appointments.
      </Typography>

      {currentBusinessId && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Current default business: <strong>{businesses.find(b => b.id === currentBusinessId)?.name || 'Unknown'}</strong>
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      ) : (
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Business Name</TableCell>
                <TableCell>Business ID</TableCell>
                <TableCell>Country</TableCell>
                <TableCell>Time Zone</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {businesses.map((business) => (
                <TableRow key={business.id} sx={{ 
                  backgroundColor: business.id === currentBusinessId ? 'rgba(76, 175, 80, 0.1)' : 'inherit'
                }}>
                  <TableCell>{business.name}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {business.id}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{business.country}</TableCell>
                  <TableCell>{business.time_zone}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button 
                        startIcon={<ContentCopyIcon />}
                        onClick={() => copyToClipboard(business.id)}
                        size="small"
                      >
                        Copy ID
                      </Button>
                      <Button 
                        startIcon={<SaveIcon />}
                        onClick={() => handleSetAsDefault(business)}
                        size="small"
                        color="primary"
                        variant={business.id === currentBusinessId ? "contained" : "outlined"}
                        disabled={business.id === currentBusinessId}
                      >
                        {business.id === currentBusinessId ? 'Default' : 'Set as Default'}
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
      >
        <DialogTitle>Set Default Business</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to set "{selectedBusiness?.name}" as the default business for appointments?
            This will affect all new appointments created in the system.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleConfirmSetDefault} color="primary" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!copySuccess}
        autoHideDuration={3000}
        onClose={() => setCopySuccess(null)}
        message={copySuccess}
      />

      <Snackbar
        open={!!saveSuccess}
        autoHideDuration={3000}
        onClose={() => setSaveSuccess(null)}
        message={saveSuccess}
      />
    </Box>
  );
};

export default BusinessSettings; 